import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser } from '../services/api';
import './Register.css';

const Register = () => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(null);

        // Validation
        if (!fullName.trim() || !email.trim() || !whatsapp.trim() || !password.trim() || !confirmPassword.trim()) {
            setError('Please fill in all fields');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(whatsapp)) {
            setError('WhatsApp number must be exactly 10 digits');
            return;
        }

        // Strong Password Validation
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(password)) {
            setError(
                'Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)'
            );
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await registerUser(fullName.trim(), email.trim(), whatsapp.trim(), password);

            if (response.success) {
                setSuccess({
                    buyerId: response.data.buyerId,
                    fullName: response.data.fullName,
                });

                // Clear form
                setFullName('');
                setEmail('');
                setWhatsapp('');
                setPassword('');
                setConfirmPassword('');
            } else {
                setError(response.message || 'Registration failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    // Handle number-only input for WhatsApp
    const handleWhatsappChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setWhatsapp(value);
    };

    return (
        <div className="register-container">
            <div className="register-card">
                <div className="register-header">
                    <h1>Collaboration World</h1>
                    <p>Create Your Account</p>
                </div>

                {success ? (
                    <div className="success-container">
                        <div className="success-icon"><i className="ri-check-line"></i></div>
                        <h2>Registration Successful!</h2>
                        <div className="credentials-box">
                            <p className="credentials-label">Your Login Credentials:</p>
                            <div className="credential-item">
                                <span className="credential-key">Buyer ID:</span>
                                <span className="credential-value">{success.buyerId}</span>
                            </div>
                            <div className="credential-item">
                                <span className="credential-key">Name:</span>
                                <span className="credential-value">{success.fullName}</span>
                            </div>
                        </div>
                        <p className="warning-text">
                            <i className="ri-error-warning-line"></i> Please save your Buyer ID. You'll need it to login.
                        </p>
                        <p className="info-text" style={{
                            background: 'rgba(99, 102, 241, 0.1)',
                            padding: '1rem',
                            borderRadius: '8px',
                            color: '#6366f1',
                            fontWeight: '600',
                            marginTop: '1rem'
                        }}>
                            <i className="ri-notification-3-line"></i> Your account is pending admin approval. You will be able to login once your account is activated.
                        </p>
                        <button onClick={handleGoToLogin} className="btn-primary">
                            Go to Login
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="register-form">
                        <div className="form-group">
                            <label htmlFor="fullName">Full Name</label>
                            <input
                                type="text"
                                id="fullName"
                                placeholder="Enter your full name"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="whatsapp">WhatsApp Phone Number</label>
                            <input
                                type="tel"
                                id="whatsapp"
                                placeholder="Enter WhatsApp number (10 digits)"
                                value={whatsapp}
                                onChange={handleWhatsappChange}
                                maxLength={10}
                                pattern="\d*"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                placeholder="Create a password (min. 6 characters)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                placeholder="Re-enter your password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Creating Account...' : 'Register'}
                        </button>
                    </form>
                )}

                {!success && (
                    <div className="register-footer">
                        <p>
                            Already have an account?
                            <br />
                            <Link to="/login" className="register-link">Login here</Link>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Register;
